import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import logo_icon from "@/assets/images/logos/ofcourse-bar-logo.svg";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import {
  apiStartModuleTest,
  type ModuleTestUnlockedModuleItem,
} from "@/services/student/module-test.api";
import { getModuleInfo } from "@/services/student/module.service";
import type { ModuleInfo } from "@/services/student/module.interfaces";

/**
 * Tela de resultado do teste do módulo.
 * Exibe passou/não passou, nota, próximo módulo desbloqueado (se passou) ou opção de refazer (se não passou).
 * Spec: add-module-test-result-screen (student-ui)
 */
export const ModuleTestResultPage = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as
    | {
        scorePercent?: number;
        passed?: boolean;
        unlockedModules?: ModuleTestUnlockedModuleItem[];
      }
    | null;
  const scorePercent = state?.scorePercent ?? 0;
  const passed = state?.passed ?? false;
  const unlockedModules: ModuleTestUnlockedModuleItem[] = state?.unlockedModules ?? [];
  const [testedModuleInfo, setTestedModuleInfo] = useState<ModuleInfo | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    getModuleInfo(moduleId)
      .then((info) => {
        if (!cancelled) setTestedModuleInfo(info);
      })
      .catch(() => {
        if (!cancelled) setTestedModuleInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  const handleRetry = async () => {
    if (!moduleId) return;
    setRetryError(null);
    setRetryLoading(true);
    try {
      const data = await apiStartModuleTest(moduleId);
      navigate(`/student/modules/${moduleId}/test`, {
        state: { sessionId: data.sessionId, exerciseIds: data.exerciseIds },
      });
    } catch {
      setRetryError("Não foi possível iniciar um novo teste. Tente novamente.");
    } finally {
      setRetryLoading(false);
    }
  };

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, []);

  return (
    <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Resultado do teste"
        subtitle={
          testedModuleInfo?.name ??
          testedModuleInfo?.title ??
          (moduleId ? `Módulo ${moduleId}` : "Módulo")
        }
        avatar={logo_icon}
        avatarAlt="Logo OfCourse"
        showActions={true}
      />

      <div className="w-full bg-gray-50 rounded-xl p-6 mt-6 flex flex-col items-center gap-6">
        {passed ? (
          <CheckCircle className="w-16 h-16 text-green-600" />
        ) : (
          <XCircle className="w-16 h-16 text-amber-600" />
        )}
        <p className="text-2xl font-semibold text-gray-800">
          {passed ? "Você passou!" : "Não passou desta vez."}
        </p>
        <p className="text-lg text-gray-600">
          Nota: {scorePercent.toFixed(0)}%
        </p>
        {passed && unlockedModules.length > 0 && (
          <div className="text-center text-gray-700 max-w-md">
            <p className="font-medium">Módulo(ões) desbloqueado(s) após aprovação:</p>
            <ul className="mt-2 text-left list-disc pl-5 space-y-1">
              {unlockedModules.map((u) => (
                <li key={u.moduleId}>
                  <span className="font-medium">
                    {u.moduleTitle?.trim() || u.moduleId}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mt-3">
              Você pode acessar pelo painel.
            </p>
          </div>
        )}
        {passed && unlockedModules.length === 0 && (
          <p className="text-center text-gray-700 max-w-md">
            Acesse o painel para ver os módulos e o pacote. Se fosse o último
            módulo da sequência, não há outro módulo a desbloquear automaticamente.
          </p>
        )}
        {!passed && (
          <p className="text-center text-gray-700">
            Você pode refazer o teste para tentar novamente (novo sorteio de questões).
          </p>
        )}
        {retryError && (
          <p className="text-sm text-red-600">{retryError}</p>
        )}
        <div className="flex flex-wrap gap-3 justify-center">
          {!passed && moduleId && (
            <Button
              onClick={handleRetry}
              disabled={retryLoading}
              variant="default"
            >
              {retryLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                "Fazer novamente"
              )}
            </Button>
          )}
          {passed && (
            <Button
              onClick={() => navigate("/student/dashboard")}
              variant="default"
            >
              Ir ao painel
            </Button>
          )}
          <Button
            onClick={() => navigate("/student/dashboard")}
            variant="secondary"
          >
            Voltar ao painel
          </Button>
        </div>
      </div>
    </div>
  );
};
